import { Injectable } from '@angular/core';
import { RecognitionFrame } from '../frame';
import { resourceString } from '../translation';

export enum TruePositiveDefinition {
    PREDICTED_ANOMALY_IS_ANOMALY = 'Anomaly', // resourceString('Anomaly')
    PREDICTED_NORMAL_IS_NORMAL = 'Normal' // resourceString('Normal')
}

export enum AdherenceToNormal {
    BestAnomalyAccuracy = 'Best Anomaly Accuracy', // resourceString('Best Anomaly Accuracy')
    SkewAnomaly = 'Skew Anomaly', // resourceString('Skew Anomaly')
    BestAverageAccuracy = 'Best Average Accuracy', // resourceString('Best Average Accuracy')
    SkewNormal = 'Skew Normal', // resourceString('Skew Normal')
    BestNormalAccuracy = 'Best Normal Accuracy' // resourceString('Best Normal Accuracy')
}

export enum AnomalyRecognitionDatasetClasses {
    Anomaly = 'Anomaly', // resourceString('Anomaly')
    Normal = 'Normal' // resourceString('Normal')
}

export enum ResultName {
    TP = 'True Positive', // resourceString('True Positive')
    TN = 'True Negative', // resourceString('True Negative')
    FP = 'False Positive', // resourceString('False Positive')
    FN = 'False Negative' // resourceString('False Negative')
}

export interface AnomalyStats {
    truePositives: number;
    falsePositives: number;
    falseNegatives: number;
    trueNegatives: number;
    totalNumberOfImages: number;
    actualAnomalyImages: number;
    actualNormalImages: number;
    predictedAnomalyImagesCorrectly: number;
    predictedNormalImagesCorrectly: number;
}

@Injectable()
export class AnomalyStatsService {
    constructor() {}

    public calculateAnomalyAccuracy(stats: AnomalyStats): number {
        return stats.actualAnomalyImages > 0
            ? (stats.predictedAnomalyImagesCorrectly / stats.actualAnomalyImages) * 100
            : 0;
    }

    public calculateNormalAccuracy(stats: AnomalyStats): number {
        return stats.actualNormalImages > 0
            ? (stats.predictedNormalImagesCorrectly / stats.actualNormalImages) * 100
            : 0;
    }

    public calculateRecall(stats: AnomalyStats): number {
        // Recall is TP / (TP + FN)
        const tpAndFn = stats.truePositives + stats.falseNegatives;
        return tpAndFn > 0 ? (stats.truePositives / tpAndFn) * 100 : 0;
    }

    public calculatePrecision(stats: AnomalyStats): number {
        // Precision is TP / (TP + FP)
        const tpAndFp = stats.truePositives + stats.falsePositives;
        return tpAndFp > 0 ? (stats.truePositives / tpAndFp) * 100 : 0;
    }

    public calculateAccuracy(stats: AnomalyStats): number {
        // Accuracy is (TP + TN) / totalNumberOfImages
        const tpAndTn = stats.truePositives + stats.trueNegatives;
        return stats.totalNumberOfImages > 0 ? (tpAndTn / stats.totalNumberOfImages) * 100 : 0;
    }

    public countStats(truePositiveDefinition: TruePositiveDefinition, recognitionFrames: RecognitionFrame[]) {
        switch (truePositiveDefinition) {
            case TruePositiveDefinition.PREDICTED_ANOMALY_IS_ANOMALY:
                return this.countStatsForAnomalyIsAnomaly(recognitionFrames);
            case TruePositiveDefinition.PREDICTED_NORMAL_IS_NORMAL:
                return this.countStatsForNormalIsNormal(recognitionFrames);
            default:
                return this.getDefaultStats();
        }
    }

    private countStatsForAnomalyIsAnomaly(recognitionFrames: RecognitionFrame[]): AnomalyStats {
        const stats = this.getDefaultStats();
        stats.totalNumberOfImages = recognitionFrames.length;

        recognitionFrames.forEach((frame: RecognitionFrame): void => {
            if (frame.actualClass === AnomalyRecognitionDatasetClasses.Anomaly) {
                stats.actualAnomalyImages++;
            }

            if (frame.actualClass === AnomalyRecognitionDatasetClasses.Normal) {
                stats.actualNormalImages++;
            }

            if (
                frame.actualClass === AnomalyRecognitionDatasetClasses.Anomaly &&
                frame.predictedClass === AnomalyRecognitionDatasetClasses.Anomaly
            ) {
                stats.truePositives++;
                stats.predictedAnomalyImagesCorrectly++;
            }

            if (
                frame.actualClass === AnomalyRecognitionDatasetClasses.Normal &&
                frame.predictedClass === AnomalyRecognitionDatasetClasses.Anomaly
            ) {
                stats.falsePositives++;
            }

            if (
                frame.actualClass === AnomalyRecognitionDatasetClasses.Anomaly &&
                frame.predictedClass === AnomalyRecognitionDatasetClasses.Normal
            ) {
                stats.falseNegatives++;
            }

            if (
                frame.actualClass === AnomalyRecognitionDatasetClasses.Normal &&
                frame.predictedClass === AnomalyRecognitionDatasetClasses.Normal
            ) {
                stats.trueNegatives++;
                stats.predictedNormalImagesCorrectly++;
            }
        });
        return stats;
    }

    private countStatsForNormalIsNormal(recognitionFrames: RecognitionFrame[]): AnomalyStats {
        const stats = this.getDefaultStats();
        stats.totalNumberOfImages = recognitionFrames.length;

        recognitionFrames.forEach((frame: RecognitionFrame): void => {
            if (frame.actualClass === AnomalyRecognitionDatasetClasses.Anomaly) {
                stats.actualAnomalyImages++;
            }

            if (frame.actualClass === AnomalyRecognitionDatasetClasses.Normal) {
                stats.actualNormalImages++;
            }

            if (
                frame.actualClass === AnomalyRecognitionDatasetClasses.Anomaly &&
                frame.predictedClass === AnomalyRecognitionDatasetClasses.Anomaly
            ) {
                stats.trueNegatives++;
                stats.predictedAnomalyImagesCorrectly++;
            }

            if (
                frame.actualClass === AnomalyRecognitionDatasetClasses.Normal &&
                frame.predictedClass === AnomalyRecognitionDatasetClasses.Anomaly
            ) {
                stats.falseNegatives++;
            }

            if (
                frame.actualClass === AnomalyRecognitionDatasetClasses.Anomaly &&
                frame.predictedClass === AnomalyRecognitionDatasetClasses.Normal
            ) {
                stats.falsePositives++;
            }

            if (
                frame.actualClass === AnomalyRecognitionDatasetClasses.Normal &&
                frame.predictedClass === AnomalyRecognitionDatasetClasses.Normal
            ) {
                stats.truePositives++;
                stats.predictedNormalImagesCorrectly++;
            }
        });
        return stats;
    }

    public getDefaultStats(): AnomalyStats {
        return {
            truePositives: 0,
            falsePositives: 0,
            falseNegatives: 0,
            trueNegatives: 0,
            actualAnomalyImages: 0,
            actualNormalImages: 0,
            predictedAnomalyImagesCorrectly: 0,
            predictedNormalImagesCorrectly: 0,
            totalNumberOfImages: 0
        };
    }

    /**
     * Take the ground truth and prediction of an anomaly dataset validation asset, and return a result based on
     * the current definition of a true positive.
     * @param actualClass The ground truth of the asset, from RecognitionFrame.actualClass.
     * @param predictedClass The predicted class of the asset, from RecognitionFrame.predictedClass.
     * @param definition The current definition of a true positive.
     */
    public getSingleAnomalyTestOutcome(
        actualClass: string,
        predictedClass: string,
        definition: TruePositiveDefinition
    ): ResultName {
        if (actualClass === 'Normal' && predictedClass === 'Normal') {
            return definition === TruePositiveDefinition.PREDICTED_NORMAL_IS_NORMAL ? ResultName.TP : ResultName.TN;
        } else if (actualClass === 'Anomaly' && predictedClass === 'Normal') {
            return definition === TruePositiveDefinition.PREDICTED_NORMAL_IS_NORMAL ? ResultName.FP : ResultName.FN;
        } else if (actualClass === 'Anomaly' && predictedClass === 'Anomaly') {
            return definition === TruePositiveDefinition.PREDICTED_NORMAL_IS_NORMAL ? ResultName.TN : ResultName.TP;
        } else if (actualClass === 'Normal' && predictedClass === 'Anomaly') {
            return definition === TruePositiveDefinition.PREDICTED_NORMAL_IS_NORMAL ? ResultName.FN : ResultName.FP;
        }

        throw new Error(`Unknown test outcome for class ${actualClass} and predicted class ${predictedClass}`);
    }

    public convertResultNameToInitials(resultName: ResultName): string {
        switch (resultName) {
            case ResultName.TP:
                return 'TP';

            case ResultName.FP:
                return 'FP';

            case ResultName.TN:
                return 'TN';

            case ResultName.FN:
                return 'FN';

            default:
                return '';
        }
    }
}



// WEBPACK FOOTER //
// ./src/providers/evaluate_brain/anomaly-stats.service.ts